import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { Globe, Menu, X, ChevronDown, ShoppingBag, Home, BookOpen, Package } from 'lucide-react';

const languages = [
  { code: 'pt', label: 'Português', flag: 'BR' },
  { code: 'en', label: 'English', flag: 'US' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
];

const langPathMap: Record<string, string> = {
  pt: '/',
  en: '/en/',
  de: '/de/',
};

const articlePathMap: Record<string, string> = {
  pt: '/artigo/',
  en: '/article/',
  de: '/artikel/',
};

const guideSlug = 'cssbuy-spreadsheet-guide';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const currentLang = i18n.language || 'pt';

  const isArticle = window.location.pathname.includes('artigo') || window.location.pathname.includes('article') || window.location.pathname.includes('artikel');

  const getSlugFromPath = (): string | null => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (isArticle && parts.length >= 2) {
      return parts[parts.length - 1];
    }
    return null;
  };

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
    const slug = getSlugFromPath();
    if (slug) {
      navigate(`${articlePathMap[code]}${slug}`);
    } else {
      navigate(langPathMap[code] || '/');
    }
    window.scrollTo(0, 0);
  };

  const navTo = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    window.scrollTo(0, 0);
  };

  const homePath = langPathMap[currentLang] || '/';
  const guidePath = `${articlePathMap[currentLang]}${guideSlug}`;
  const productsPath = '/products';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-brand-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href={homePath}
            onClick={(e) => { e.preventDefault(); navTo(homePath); }}
            className="flex items-center gap-2.5 text-warm-900 hover:text-brand-700 transition-colors"
          >
            <ShoppingBag className="w-6 h-6 text-brand-600" />
            <span className="font-display font-bold text-lg md:text-xl tracking-tight text-warm-900">CSSBuy</span>
            <span className="hidden sm:inline text-xs font-sans font-semibold text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full">Spreadsheet</span>
          </a>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={homePath}
              onClick={(e) => { e.preventDefault(); navTo(homePath); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${!isArticle ? 'text-white bg-brand-600 hover:bg-brand-700' : 'text-warm-700 hover:text-brand-700 hover:bg-brand-50'}`}
            >
              <Home className="w-4 h-4" />
              {t('home')}
            </a>
            <a
              href={guidePath}
              onClick={(e) => { e.preventDefault(); navTo(guidePath); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${isArticle ? 'text-white bg-brand-600 hover:bg-brand-700' : 'text-warm-700 hover:text-brand-700 hover:bg-brand-50'}`}
            >
              <BookOpen className="w-4 h-4" />
              {t('shoppingGuide')}
            </a>
            <a
              href={productsPath}
              onClick={(e) => { e.preventDefault(); navTo(productsPath); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${window.location.pathname === '/products' ? 'text-white bg-brand-600 hover:bg-brand-700' : 'text-warm-700 hover:text-brand-700 hover:bg-brand-50'}`}
            >
              <Package className="w-4 h-4" />
              {t('products')}
            </a>

            <div className="relative ml-1">
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-warm-700 hover:text-brand-700 hover:bg-brand-50 transition-colors"
              >
                <span>{t('categories')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-white rounded-xl border border-warm-200 shadow-lg py-2 max-h-[70vh] overflow-y-auto">
                  <div className="px-3 py-1.5 text-xs font-semibold text-warm-400 uppercase tracking-wider">{t('selectCategory')}</div>
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2 text-sm text-warm-700 hover:text-brand-700 hover:bg-brand-50 transition-colors"
                      onClick={() => setCatOpen(false)}
                    >
                      <img src={cat.image} alt={cat.name} className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative ml-1">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-warm-700 hover:text-brand-700 hover:bg-brand-50 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs font-semibold">{currentLang}</span>
              </button>
              {langOpen && (
                <div className="absolute top-full mt-2 right-0 w-40 bg-white rounded-xl border border-warm-200 shadow-lg py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${currentLang === lang.code ? 'text-brand-700 bg-brand-50 font-medium' : 'text-warm-700 hover:text-brand-700 hover:bg-brand-50'}`}
                    >
                      <span className="text-base">{lang.flag === 'BR' ? '🇧🇷' : lang.flag === 'US' ? '🇬🇧' : '🇩🇪'}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            className="md:hidden p-2 rounded-lg text-warm-700 hover:bg-brand-50 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t-2 border-brand-100 bg-white max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <a
                href={homePath}
                onClick={(e) => { e.preventDefault(); navTo(homePath); }}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold bg-brand-600 text-white"
              >
                <Home className="w-4 h-4" /> {t('home')}
              </a>
              <a
                href={guidePath}
                onClick={(e) => { e.preventDefault(); navTo(guidePath); }}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold bg-brand-50 text-brand-700 border border-brand-200"
              >
                <BookOpen className="w-4 h-4" /> {t('shoppingGuide')}
              </a>
              <a
                href={productsPath}
                onClick={(e) => { e.preventDefault(); navTo(productsPath); }}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border ${window.location.pathname === '/products' ? 'bg-brand-600 text-white border-brand-600' : 'bg-brand-50 text-brand-700 border-brand-200'}`}
              >
                <Package className="w-4 h-4" /> {t('products')}
              </a>
            </div>

            <div className="border-t border-warm-100">
              <div className="px-1 py-2 text-xs font-semibold text-warm-400 uppercase tracking-wider">{t('categories')}</div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2 rounded-lg border border-warm-200 hover:border-brand-300 hover:bg-brand-50 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <img src={cat.image} alt={cat.name} className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                    <span className="text-xs text-warm-700 leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-warm-100 pt-3">
              <div className="px-1 py-1 text-xs font-semibold text-warm-400 uppercase tracking-wider">{t('language')}</div>
              <div className="grid grid-cols-3 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${currentLang === lang.code ? 'text-brand-700 bg-brand-50 border-brand-200' : 'text-warm-600 border-warm-200 hover:bg-brand-50'}`}
                  >
                    <span>{lang.flag === 'BR' ? '🇧🇷' : lang.flag === 'US' ? '🇬🇧' : '🇩🇪'}</span>
                    <span className="uppercase font-semibold">{lang.code}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}